/**
 * Metric configuration for dashboard display
 * Defines titles, units, icons, and descriptions for all device metrics
 */

export interface MetricConfig {
  title: string;
  suffix: string;
  icon: string;
  description: string;
}

/**
 * Map metric keys to display configuration
 */
export const METRIC_CONFIG: Record<string, MetricConfig> = {
  // Fermentor/Bioprocess metrics
  lt: {
    title: "Level Transmitter",
    suffix: "L",
    icon: "📊",
    description: "Real-time fluid level monitoring"
  },
  qt: {
    title: "Flow Rate",
    suffix: "L/min",
    icon: "💧",
    description: "Volumetric flow rate measurement"
  },
  ph: {
    title: "pH Balance",
    suffix: "pH",
    icon: "⚗️",
    description: "Acidity/alkalinity level"
  },
  tt: {
    title: "Temperature",
    suffix: "°C",
    icon: "🌡️",
    description: "Core temperature sensor"
  },
  wt: {
    title: "Mass Load",
    suffix: "kg",
    icon: "⚖️",
    description: "Total weight measurement"
  },

  // System/Host metrics
  ram_used_mb: {
    title: "RAM Used",
    suffix: "MB",
    icon: "💾",
    description: "Current RAM usage in megabytes"
  },
  ram_total_mb: {
    title: "RAM Total",
    suffix: "MB",
    icon: "💾",
    description: "Total RAM available"
  },
  ram_usage_percent: {
    title: "RAM Usage",
    suffix: "%",
    icon: "💾",
    description: "RAM usage percentage"
  },
  cpu_usage_percent: {
    title: "CPU Usage",
    suffix: "%",
    icon: "🖥️",
    description: "CPU usage percentage"
  },
  cpu_cores: {
    title: "CPU Cores",
    suffix: "",
    icon: "🖥️",
    description: "Number of CPU cores"
  },

  // Power/Mixer metrics
  power: {
    title: "Power Consumption",
    suffix: "W",
    icon: "⚡",
    description: "Real-time power draw"
  },
  power_reconstruction: {
    title: "Power Prediction (AI)",
    suffix: "W",
    icon: "🤖",
    description: "Real-time AI power prediction"
  },
  energy: {
    title: "Energy Usage",
    suffix: "kWh",
    icon: "🔋",
    description: "Cumulative energy consumption"
  },
  state: {
    title: "Process State",
    suffix: "",
    icon: "🔄",
    description: "Current operational state"
  },

  // Generic system metrics
  cpu: {
    title: "CPU Usage",
    suffix: "%",
    icon: "🖥️",
    description: "Processor utilization"
  },
  gpu: {
    title: "GPU Usage",
    suffix: "%",
    icon: "🎮",
    description: "Graphics processing load"
  },
  ram: {
    title: "RAM Usage",
    suffix: "MB",
    icon: "💾",
    description: "Memory allocation"
  },
  cpu_temp: {
    title: "CPU Temperature",
    suffix: "°C",
    icon: "🌡️",
    description: "Processor thermal state"
  },
};

/**
 * Metrics that indicate power/mixer capability
 */
export const POWER_METRICS = ['power', 'power_reconstruction', 'energy', 'state'];

/**
 * Metrics that indicate fermentor/bioprocess capability
 */
export const FERMENTOR_METRICS = ['lt', 'qt', 'ph', 'tt', 'wt'];

/**
 * System metrics (CPU/RAM) - typically excluded from metric cards
 */
export const SYSTEM_METRICS = ['cpu_usage_percent', 'ram_usage_percent', 'ram_used_mb', 'ram_total_mb', 'cpu_cores'];

/**
 * Get metric configuration by key
 */
export function getMetricConfig(key: string): MetricConfig | undefined {
  return METRIC_CONFIG[key];
}

/**
 * Check if a metric key is a power-related metric
 */
export function isPowerMetric(key: string): boolean {
  return POWER_METRICS.includes(key);
}

/**
 * Check if a metric key is a fermentor-related metric
 */
export function isFermentorMetric(key: string): boolean {
  return FERMENTOR_METRICS.includes(key);
}

/**
 * Check if a metric key is a system metric
 */
export function isSystemMetric(key: string): boolean {
  return SYSTEM_METRICS.includes(key);
}
